import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import { isInvalidDate } from 'lib/CustomLuxonUtils'
import PGATimePicker from 'components/pga-time-picker'
import EventIcon from 'components/material-symbol/icons/Event'
import {
  outlinedPickerFieldSx,
  OUTLINED_INPUT_BORDER_RADIUS,
} from 'lib/form-field-styles'
import { useStyles } from '../styles'

const hasValidDateTimeValue = value => value != null && !isInvalidDate(value)

const defaultOpenTimeFromDate = dateValue => {
  const date = DateTime.isDateTime(dateValue)
    ? dateValue
    : DateTime.fromJSDate(dateValue)
  return date.set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
}

const RegistrationOpenDateTime = () => {
  const classes = useStyles()
  const { control, watch, setValue, errors, register } = useFormContext()
  const registrationOpenDate = watch('registrationOpenDate')
  const registrationOpenTime = watch('registrationOpenTime')

  useEffect(() => {
    if (
      hasValidDateTimeValue(registrationOpenDate) &&
      !hasValidDateTimeValue(registrationOpenTime)
    ) {
      setValue(
        'registrationOpenTime',
        defaultOpenTimeFromDate(registrationOpenDate),
        { shouldValidate: true },
      )
    }

    if (
      !hasValidDateTimeValue(registrationOpenDate) &&
      hasValidDateTimeValue(registrationOpenTime)
    ) {
      setValue('registrationOpenTime', null, { shouldValidate: true })
    }
  }, [registrationOpenDate, registrationOpenTime, setValue])

  const dateError = errors?.registrationOpenDate?.message
  const timeError = errors?.registrationOpenTime?.message
  const showTimePicker = hasValidDateTimeValue(registrationOpenDate)
  const timePickerValue = hasValidDateTimeValue(registrationOpenTime)
    ? registrationOpenTime
    : showTimePicker
      ? defaultOpenTimeFromDate(registrationOpenDate)
      : null

  return (
    <Box className={classes.fieldGroup} width="100%">
      <Typography className={classes.fieldHelper}>
        Leave blank to open registration immediately.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={showTimePicker ? 6 : 12}>
          <LocalizationProvider
            dateAdapter={CustomLuxonAdapter}
            dateFormats={{ normalDateWithWeekday: 'LLL d, yyyy' }}
          >
            <ThemeProvider
              theme={createTheme(calendarDatePickerTheme, {
                shape: { borderRadius: OUTLINED_INPUT_BORDER_RADIUS },
              })}
            >
              <Controller
                control={control}
                name="registrationOpenDate"
                defaultValue={null}
                render={({ onChange, value }) => (
                  <DesktopDatePicker
                    label="Registration opens"
                    name="registrationOpenDate"
                    innerRef={register}
                    value={value}
                    onChange={newDate => {
                      onChange(newDate)
                      if (hasValidDateTimeValue(newDate)) {
                        if (!hasValidDateTimeValue(registrationOpenTime)) {
                          setValue(
                            'registrationOpenTime',
                            defaultOpenTimeFromDate(newDate),
                            { shouldValidate: true },
                          )
                        }
                      } else {
                        setValue('registrationOpenTime', null, {
                          shouldValidate: true,
                        })
                      }
                    }}
                    disablePast
                    format="LLL d, yyyy"
                    dayOfWeekFormatter={day => `${day}`}
                    closeOnSelect
                    sx={{ width: '100%' }}
                    slots={mergeDesktopDatePickerSlots({
                      openPickerIcon: EventIcon,
                    })}
                    slotProps={mergeDesktopDatePickerSlotProps({
                      textField: {
                        sx: outlinedPickerFieldSx,
                        error: !!dateError || !!timeError,
                        helperText: dateError || timeError || null,
                      },
                      field: { clearable: true },
                    })}
                  />
                )}
              />
            </ThemeProvider>
          </LocalizationProvider>
        </Grid>
        {showTimePicker ? (
          <Grid item xs={12} sm={6}>
            <PGATimePicker
              id="registration-open-time"
              name="registrationOpenTime"
              defaultValue={timePickerValue}
              date={
                DateTime.isDateTime(registrationOpenDate)
                  ? registrationOpenDate
                  : DateTime.fromJSDate(registrationOpenDate)
              }
              error={!!timeError}
              helperText={timeError}
              onChanged={() => {}}
              label="Time"
              minutesStep={5}
              format="h:mm a"
              slotProps={{
                textField: {
                  sx: outlinedPickerFieldSx,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  )
}

export default RegistrationOpenDateTime
